// Purpose: Reusable AI Analysis component that can be attached to evidence windows
// Audience: Any evidence display that needs AI analysis functionality
// Features:
// - Three tabs: Scan, History, Your Results
// - Analysis configuration and execution
// - Results viewing and management

import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { FaRobot, FaHistory, FaPlay, FaFolder, FaUser, FaEdit } from 'react-icons/fa';
import AnalysisLog from './AnalysisLog';
import AnalysisConfigurationMenu from './AnalysisConfigurationMenu';

import '../../styles/AnalysisModal.css';
import { useNotifications } from '../notifications/NotificationsProvider';

const AIAnalysisComponent = ({ 
  evidence, 
  casefileId, 
  casefileName, 
  currentUser,
  onUpdateSuccess,
  evidenceSummary,
  onSummaryChange,
  isEditingSummary,
  onEditingSummaryChange,
  isSavingSummary,
  onSavingSummaryChange,
  initialTab = 'selected',
  navigationTrigger
}) => {
  const { notify } = useNotifications();
  const [activeAnalysisTab, setActiveAnalysisTab] = useState(initialTab);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showConfigMenu, setShowConfigMenu] = useState(false);

  // Debug: Log when component mounts and state changes
  useEffect(() => {
    console.log('🤖 AIAnalysisComponent mounted', {
      evidence: evidence?.id,
      casefileId,
      initialTab,
      showConfigMenu
    });
  }, []);

  useEffect(() => {
    console.log('🔧 showConfigMenu state changed:', showConfigMenu);
  }, [showConfigMenu]);

  // Update active tab when initialTab prop changes (for direct navigation)
  useEffect(() => {
    setActiveAnalysisTab(initialTab);
  }, [initialTab, navigationTrigger]);

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    try {
      if (timestamp && typeof timestamp.toDate === 'function') {
        return new Date(timestamp.toDate()).toLocaleString();
      }
      if (timestamp instanceof Date) {
        return timestamp.toLocaleString();
      }
      return new Date(timestamp).toLocaleString();
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  };

  const handleCancelSummaryEdit = () => {
    if (onSummaryChange) {
      onSummaryChange(evidence?.summary || '');
    }
    if (onEditingSummaryChange) {
      onEditingSummaryChange(false);
    }
  };

  const handleSaveSummary = async () => {
    if (evidenceSummary.trim() === (evidence?.summary || '')) {
      if (onEditingSummaryChange) {
        onEditingSummaryChange(false);
      }
      return;
    }

    if (onSavingSummaryChange) {
      onSavingSummaryChange(true);
    }
    try {
      const evidenceRef = doc(db, 'casefiles', casefileId, 'evidence', evidence.id);
      await updateDoc(evidenceRef, {
        summary: evidenceSummary.trim()
      });
      
      const updatedEvidence = { ...evidence, summary: evidenceSummary.trim() };
      if (onUpdateSuccess) {
        onUpdateSuccess(updatedEvidence);
      }
      
      if (onEditingSummaryChange) {
        onEditingSummaryChange(false);
      }
    } catch (error) {
      console.error('Error updating summary:', error);
      notify({
        severity: 'error',
        title: 'Update Failed',
        message: 'Failed to update summary. Please try again.'
      });
    } finally {
      if (onSavingSummaryChange) {
        onSavingSummaryChange(false);
      }
    }
  };

  const handleSummaryKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveSummary();
    } else if (e.key === 'Escape') {
      handleCancelSummaryEdit();
    }
  };

  const handleAnalysisStarted = (analysisData) => {
    console.log('Professional analysis job initiated:', analysisData);
    setShowConfigMenu(false);
    setActiveAnalysisTab('history');
    
    notify({
      severity: 'success',
      title: 'Analysis Launched',
      message: `Job ${analysisData.analysisJobId} started. Est. cost $${analysisData.estimatedCost}.`
    });
  };

  return (
    <div className="evidence-analysis-section">
      <div className="analysis-header-with-tabs">
        <h4 style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          marginBottom: '15px',
          color: '#fff'
        }}>
          <FaRobot style={{ color: '#8b5cf6' }} />
          AI Analysis
          {selectedAnalysis && activeAnalysisTab === 'selected' && (
            <span style={{ 
              fontSize: '0.7em', 
              color: '#888', 
              fontWeight: 'normal',
              marginLeft: '10px'
            }}>
              {formatTimestamp(selectedAnalysis?.completedAt || selectedAnalysis?.initiatedAt)}
            </span>
          )}
        </h4>

        {/* Analysis Tabs */}
        <div className="analysis-tabs">
          <button 
            className={`analysis-tab ${activeAnalysisTab === 'selected' ? 'active' : ''}`}
            onClick={() => setActiveAnalysisTab('selected')}
          >
            <FaPlay />
            Scan
          </button>
          <button 
            className={`analysis-tab ${activeAnalysisTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveAnalysisTab('history')}
          >
            <FaHistory />
            History
            {evidence.analysisCount > 0 && (
              <span className="analysis-count-badge">{evidence.analysisCount}</span>
            )}
          </button>
          <button 
            className={`analysis-tab ${activeAnalysisTab === 'yourResults' ? 'active' : ''}`}
            onClick={() => setActiveAnalysisTab('yourResults')}
          >
            <FaUser />
            Your Results
          </button>
        </div>
      </div>

      {/* Scan Tab */}
      {activeAnalysisTab === 'selected' && (
        <div>
          {!showConfigMenu ? (
            <div className="ai-cta-panel">
              {/* Row 1: Robot left, Title right on same line */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <FaRobot className="ai-cta-robot" style={{ marginBottom: 0, color: '#8b5cf6', fontSize: '4rem' }} />
                <h3 style={{ margin: 0, fontWeight: 700 }}>Professional AI Analysis</h3>
              </div>
              {/* Row 2: Centered subtitle */}
              <div style={{ textAlign: 'center', marginBottom: 16, opacity: 0.9, fontSize: '1rem', color: '#cbd5e1' }}>
                Configure advanced analysis parameters
              </div>
              {/* Row 3: Buttons centered with space between */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1000 }}>
                <button 
                  onClick={() => {
                    console.log('🔵 Configure button clicked!');
                    setShowConfigMenu(true);
                  }}
                  className="ai-cta-btn ai-cta-primary"
                  style={{ position: 'relative', zIndex: 1001, pointerEvents: 'auto' }}
                >
                  Configure
                </button>
                {/* Quick Scan Button */}
                <button
                  onClick={async () => {
                    try {
                      const createOrder = httpsCallable(functions, 'createAnalysisOrder');
                      const orderResult = await createOrder({
                        evidenceId: evidence.id,
                        casefileId: casefileId,
                        scanTier: 1,
                        modelOverrides: {}
                      });

                      if (orderResult.data.success) {
                        handleAnalysisStarted(orderResult.data);
                      }
                    } catch (error) {
                      console.error('Error starting quick scan:', error);
                      notify({
                        severity: 'error',
                        title: 'Quick Scan Failed',
                        message: error.message || 'Could not start quick scan.'
                      });
                    }
                  }}
                  className="ai-cta-btn ai-cta-secondary"
                  style={{ padding: '10px 16px', fontSize: '0.9rem' }}
                >
                  Quick Scan →
                </button>
              </div>
            </div>
          ) : (
            <AnalysisConfigurationMenu
              evidence={evidence}
              casefileId={casefileId}
              onClose={() => setShowConfigMenu(false)}
              onAnalysisStarted={handleAnalysisStarted}
            />
          )}
        </div>
      )}

      {/* History Tab */}
      {activeAnalysisTab === 'history' && (
        <div className="analysis-history-content">
          <AnalysisLog 
            evidenceId={evidence.id}
            casefileId={casefileId}
            currentUser={currentUser}
            onSelectAnalysis={setSelectedAnalysis}
          />
        </div>
      )}

      {/* Your Results Tab - Shows only current user's analysis */}
      {activeAnalysisTab === 'yourResults' && (
        <div className="analysis-history-content">
          <AnalysisLog 
            evidenceId={evidence.id}
            casefileId={casefileId}
            currentUser={currentUser}
            onSelectAnalysis={setSelectedAnalysis}
          />
          {!currentUser && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
              Please sign in to view your analysis results
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default AIAnalysisComponent;
